import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import Header from 'shared/components/Header';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import usePersistedColumnVisibility from 'shared/hooks/usePersistedColumnVisibility';
import { tokens } from 'shared/theme';
import { useAuth } from 'shared/hooks/useAuth';
import { canViewAllRecords } from 'shared/utils/canViewAllRecords';
import { buildGridToolbarExportProps } from 'shared/utils/tableExportFileName';
import useGroupService from 'shared/api/services/groupService';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import CreateGroupDialog from './createGroupDialog';
import EditGroupDialog from './editGroupDialog';
import DeleteGroupDialog from './deleteGroupDialog';
const GroupPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useAuth();
  const canViewAll = canViewAllRecords(user);
  const { columnVisibilityModel, onColumnVisibilityModelChange } =
    usePersistedColumnVisibility('platform.groups');
  const {
    groups,
    totalRecords,
    isLoading,
    fetchGroups,
    createGroup,
    updateGroup,
    changeGroupStatus,
  } = useGroupService();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => {
    fetchGroups(
      paginationModel.page,
      paginationModel.pageSize,
      canViewAll && showAll ? null : 'ACTIVE'
    );
  }, [canViewAll, fetchGroups, paginationModel.page, paginationModel.pageSize, showAll]);

  useEffect(() => {
    if (!canViewAll && showAll) {
      setShowAll(false);
    }
  }, [canViewAll, showAll]);

  useEffect(() => {
    load();
  }, [load]);

  const handleEdit = useCallback((row) => setEditRow(row), []);
  const handleDeleteClick = useCallback((row) => setDeleteTarget(row), []);
  const openGroupPermissions = useCallback(
    (row) => {
      if (row?.id) navigate(`/groups/${row.id}/permissions`);
    },
    [navigate]
  );

  const rows = useMemo(
    () =>
      groups.map((g) => ({
        id: g.id,
        name: g.name ?? '',
        serviceId: g.serviceId ?? '',
        serviceName: g.serviceName ?? '',
        serviceCode: g.serviceCode ?? '',
        serviceLabel:
          g.serviceName && g.serviceCode
            ? `${g.serviceName} (${g.serviceCode})`
            : g.serviceName || g.serviceCode || '',
        description: g.description ?? '',
        status: g.status ?? '',
        createdAt: g.createdAt ?? '',
      })),
    [groups]
  );

  const columns = useMemo(
    () => [
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 140 },
      { field: 'serviceLabel', headerName: 'Service', flex: 0.8, minWidth: 130 },
      { field: 'description', headerName: 'Description', flex: 1.5, minWidth: 180 },
      { field: 'status', headerName: 'Status', width: 120 },
      { field: 'createdAt', headerName: 'Created', width: 160 },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 160,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box display="flex" alignItems="center" gap={0.25}>
            <Tooltip title="View / assign permissions">
              <IconButton
                size="small"
                color="info"
                onClick={() => openGroupPermissions(row)}
                aria-label="Group permissions"
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleEdit(row)}
                aria-label="Edit group"
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Mark as deleted">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteClick(row)}
                aria-label="Delete group"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [handleEdit, handleDeleteClick, openGroupPermissions]
  );

  return (
    <Box m="20px">
      <CreateGroupDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={load}
        createGroup={createGroup}
      />
      <EditGroupDialog
        open={Boolean(editRow)}
        onClose={() => setEditRow(null)}
        onSuccess={load}
        group={editRow}
        updateGroup={updateGroup}
      />
      <DeleteGroupDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onSuccess={load}
        group={deleteTarget}
        changeGroupStatus={changeGroupStatus}
      />

      <Header title="Groups" subtitle="Access control — tenant" />
      <Box mt={2} mb={2} display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={1}>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AssessmentOutlinedIcon />}
            onClick={() => navigate('users-permissions-report')}
            sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
          >
            Users Permissions Report
          </Button>
          {canViewAll ? (
          <Button
            variant={showAll ? 'contained' : 'outlined'}
            color="secondary"
            onClick={() => {
              setPaginationModel((p) => ({ ...p, page: 0 }));
              setShowAll((v) => !v);
            }}
            sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
          >
            {showAll ? 'Active only' : 'View all'}
          </Button>
          ) : null}
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setCreateOpen(true)}
            sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
          >
            Create group
          </Button>
        </Box>
      </Box>
      <Box mt={0} position="relative">
        {isLoading && (
          <Box
            position="absolute"
            zIndex={2}
            left="50%"
            top="40%"
            sx={{ transform: 'translate(-50%, -50%)' }}
          >
            <LoadingSpinner />
          </Box>
        )}
        <Box
          height="72vh"
          sx={{
            opacity: isLoading ? 0.4 : 1,
            pointerEvents: isLoading ? 'none' : 'auto',
            '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
              color: `${colors.grey[100]} !important`,
            },
          }}
        >
          <DataGrid
            disableRowSelectionOnClick
            rows={rows}
            columns={columns}
            rowCount={totalRecords}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: buildGridToolbarExportProps({
                title: 'Groups',
                tableId: 'platform.groups',
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              }),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default GroupPage;
